import { useNavigate } from "react-router-dom";

import { useSession } from "@/auth";
import { cx } from "@/lib/cx";
import { Button } from "@/ui/Button";
import { Menu, MenuItem, MenuSeparator } from "@/ui/overlay/Menu";
import { AccountIcon, AgentDeskIcon, ChevronDownIcon, SignOutIcon } from "@/ui/icons";
import { ConnectionDot } from "./ConnectionStatus";

export type AccountMenuProps = {
  /**
   * `full` shows the signed-in address and reads as a row in the sidebar
   * footer. `icon` is a 44px account orb for the rail and phone corner.
   */
  variant?: "full" | "icon";
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  /** Account orbs are round; a labelled row may use the control radius. */
  triggerCorners?: "pill" | "control";
  className?: string;
};

/**
 * Everything about *the person using the app*, behind one control.
 *
 * Sign out used to sit in the top-right corner of the phone header — the most
 * valuable 80×32 pixels on the screen — as a plain text button next to nothing
 * else (UI-REVIEW §4). It is the least-used control in the product and the only
 * one that throws away your session; it does not get to be the easiest thing to
 * hit with a thumb. Putting it one tap deeper costs a daily driver nothing and
 * removes an accidental logout entirely.
 *
 * `danger` tone on the item, not a confirm dialog: signing out is recoverable
 * with a password, and the app reserves dialogs for what genuinely is not.
 */
export function AccountMenu({
  variant = "full",
  side = "top",
  align = "start",
  triggerCorners = "pill",
  className,
}: AccountMenuProps) {
  const { user, signOut } = useSession();
  const navigate = useNavigate();
  const email = typeof user?.email === "string" ? user.email : "Signed in";
  const name = typeof user?.name === "string" && user.name.trim() ? user.name.trim() : email.split("@")[0] || "Player";

  const trigger =
    variant === "icon" ? (
      <Button
        variant="ghost"
        size="md"
        corners={triggerCorners}
        iconOnly
        aria-label={`Account — ${email}`}
        className={cx("min-h-tap min-w-tap", className)}
      >
        <AccountIcon className="account-orb-icon size-5" />
        {/* Shown only where the control is a full-width rail command (shell.css
            `.pixel-account-orb`); the phone corner stays a bare icon. */}
        <span className="account-orb-label hidden">{name}</span>
        <ConnectionDot compact badge className="account-orb-status" />
      </Button>
    ) : (
      <Button
        variant="ghost"
        size="md"
        corners={triggerCorners}
        fullWidth
        align="start"
        aria-label={`Account — ${email}`}
        className={className}
      >
        <AccountIcon className="size-4 shrink-0" />
        <span className="min-w-0 flex-1 truncate">{email}</span>
        <ChevronDownIcon className="size-3 shrink-0 text-faint" />
      </Button>
    );

  return (
    <Menu trigger={trigger} side={side} align={align} label="Account" className="ps1-account-menu">
      <MenuItem disabled icon={<AccountIcon className="size-4" />} onSelect={() => undefined}>
        {email}
      </MenuItem>
      <MenuSeparator />
      <MenuItem icon={<AccountIcon className="size-4" />} onSelect={() => navigate("/more")}>
        Profile and picture
      </MenuItem>
      <MenuItem icon={<AgentDeskIcon className="size-4" />} onSelect={() => navigate("/connect")}>
        Connect Claude Code
      </MenuItem>
      <MenuSeparator />
      <MenuItem tone="danger" icon={<SignOutIcon className="size-4" />} onSelect={signOut}>
        Sign out
      </MenuItem>
    </Menu>
  );
}
